/* tslint:disable object-literal-sort-keys */
import React from 'react';

import { Button } from 'react-bootstrap';

import { Icon } from '../../common';
import { TableRenderingUtils } from '../../../utils';
import { IReactTableColumn } from '../../../interfaces';
import { IOption } from '../../../stores/OptionsStoreClass';

// The second header row of the liens table carries the totals; these fill the same slots here.
const {
  renderDate,
  renderMoney,
  renderOptionLabel,
  renderSummedAmount,
  renderTotalRowCount,
  renderUnformatted,
} = TableRenderingUtils;

// The whole cell is the click target -- LoansTable hands the click to the row through getTdProps --
// so this renders the affordance and nothing else, as renderViewFunding does on the liens table.
function renderViewLoan () {
  return (
    <div>
      <Button bsStyle='link' className='icon-right'>
        View <Icon type='angle-double-right' />
      </Button>
    </div>
  );
}

// Red, as the delete button in the loan form is: what it leads to changes or removes a loan that the
// account's payments and compliance have already been worked out from.
function renderManageLoan (label: string) {
  return (
    <div>
      <Button bsStyle='link' className='btn-manage-loan'>{label}</Button>
    </div>
  );
}

/**
 * Built per option list rather than declared as a constant, as the collateral cases table's columns
 * are: the status column renders the label the API gave the value, not the raw enum.
 *
 * `manageLabel` is what the row offers beside View -- 'Edit' for a user who may change a loan,
 * 'Delete' for one who may only remove it, and nothing at all for everyone else, which is almost
 * every user. The column is left out entirely rather than rendered empty.
 */
export function loanColumns (statusOptions: IOption[], manageLabel: null | string): IReactTableColumn[] {
  const manageColumn = {
    columns: [{
      className: 'manage-loan-column',
      Cell: () => renderManageLoan(manageLabel as string),
      sortable: false,
      width: 80,
    }],
  };

  return [
    // Leftmost, ahead of the id, as on the liens tables. The cell is right-aligned within its own
    // width, so the link sits snug against the column beside it.
    {
      columns: [{
        className: 'view-loan-column',
        Cell: renderViewLoan,
        sortable: false,
        width: 100,
      }],
    },
    ...(manageLabel ? [manageColumn] : []),
    {
      Header: 'Loan Id',
      columns: [{
        accessor: 'vanity_id',
        Cell: renderUnformatted,
        minWidth: 150,
        maxWidth: 200,
        sortable: true,
      }],
    },
    {
      Header: 'Origination',
      columns: [{
        Header: renderTotalRowCount,
        accessor: 'origination_date',
        Cell: renderDate,
        minWidth: 120,
        maxWidth: 200,
      }],
    },
    {
      Header: 'Initial Principal',
      columns: [{
        Header: renderSummedAmount,
        accessor: 'initial_principal_amount',
        Cell: renderMoney,
        minWidth: 140,
        maxWidth: 200,
      }],
    },
    // Rightmost, so the three columns describing what the loan is stay together to its left. A
    // closed loan is the one thing about a row that the figures beside it do not say.
    {
      Header: 'Status',
      columns: [{
        accessor: 'status',
        Cell: renderOptionLabel.bind(null, statusOptions),
        minWidth: 120,
        maxWidth: 200,
        sortable: true,
      }],
    },
  ];
}
